import { getAncestors } from "./getAncestors";
import { getDescendants } from "./getDescendants";
import { nodeMatchesSearch } from "./nodeMatchesSearch";

/**
 * Filters hierarchy data based on a search term
 * Returns filtered data and items that should be expanded
 *
 * @param {Object} hierarchyData - The complete hierarchy data object
 * @param {string} searchTerm - The search term to filter by
 * @returns {Object} Object containing filteredData and filteredExpandedItems
 */
export const filterHierarchyBySearch = (hierarchyData, searchTerm) => {
	if (!searchTerm || !hierarchyData) {
		return { filteredData: hierarchyData, filteredExpandedItems: [] };
	}

	const matchingNodeIds = new Set();
	const nodesToShow = new Set();
	const ancestorsToExpand = new Set();

	// Find all nodes that match the search
	Object.keys(hierarchyData).forEach(nodeId => {
		if (nodeId === 'root') return;

		const node = hierarchyData[nodeId];
		if (nodeMatchesSearch(node, searchTerm)) {
			matchingNodeIds.add(nodeId);
			nodesToShow.add(nodeId);

			// Add all ancestors of matching nodes and mark them for expansion
			const ancestors = getAncestors(nodeId, hierarchyData);
			ancestors.forEach(ancestorId => {
				nodesToShow.add(ancestorId);
				ancestorsToExpand.add(ancestorId);
			});

			// Add all descendants of matching nodes (but keep them collapsed)
			const descendants = getDescendants(nodeId, hierarchyData, true);
			descendants.forEach(descId => nodesToShow.add(descId));
		}
	});

	// Build filtered hierarchy
	const filtered = { root: { ...hierarchyData.root } };

	nodesToShow.forEach(nodeId => {
		const node = hierarchyData[nodeId];
		filtered[nodeId] = {
			...node,
			children: node.children?.filter(childId => nodesToShow.has(childId)) || []
		};
	});

	// Update root children
	filtered.root.children = hierarchyData.root.children?.filter(childId => nodesToShow.has(childId)) || [];

	return { filteredData: filtered, filteredExpandedItems: Array.from(ancestorsToExpand) };
};
